'use client';

import { useState, useEffect, useCallback } from 'react';

export type ReadingTheme = 'dark' | 'light' | 'sepia';
export type FontFamily = 'sans' | 'serif';
export type FontSize = 'small' | 'medium' | 'large' | 'xlarge';

interface ReadingModePreferences {
  theme: ReadingTheme;
  fontFamily: FontFamily;
  fontSize: FontSize;
  lineHeight: 'compact' | 'normal' | 'relaxed';
}

const STORAGE_KEY = 'reading-mode-prefs';

const DEFAULT_PREFS: ReadingModePreferences = {
  theme: 'dark',
  fontFamily: 'serif',
  fontSize: 'medium',
  lineHeight: 'normal',
};

export const FONT_SIZE_MAP: Record<FontSize, string> = {
  small: '16px',
  medium: '18px',
  large: '20px',
  xlarge: '22px',
};

export const LINE_HEIGHT_MAP: Record<string, string> = {
  compact: '1.6',
  normal: '1.8',
  relaxed: '2.0',
};

export const THEME_MAP: Record<ReadingTheme, { bg: string; text: string; accent: string }> = {
  dark: { bg: '#0A1E2E', text: '#E8D5B7', accent: '#E89841' },
  light: { bg: '#FAFAF8', text: '#1a1a1a', accent: '#c77a2a' },
  sepia: { bg: '#F4ECD8', text: '#5B4636', accent: '#8B6914' },
};

function loadPrefs(): ReadingModePreferences {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...DEFAULT_PREFS, ...JSON.parse(stored) };
  } catch {}
  return DEFAULT_PREFS;
}

export function useReadingMode() {
  const [isActive, setIsActive] = useState(false);
  const [prefs, setPrefs] = useState<ReadingModePreferences>(DEFAULT_PREFS);

  useEffect(() => {
    setPrefs(loadPrefs());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {}
  }, [prefs]);

  // Lock body scroll when reading mode is active
  useEffect(() => {
    if (isActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isActive]);

  // Escape key to exit
  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsActive(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive]);

  const toggle = useCallback(() => setIsActive(prev => !prev), []);
  const enter = useCallback(() => setIsActive(true), []);
  const exit = useCallback(() => setIsActive(false), []);

  const setTheme = useCallback((theme: ReadingTheme) => {
    setPrefs(p => ({ ...p, theme }));
  }, []);

  const setFontFamily = useCallback((fontFamily: FontFamily) => {
    setPrefs(p => ({ ...p, fontFamily }));
  }, []);

  const setFontSize = useCallback((fontSize: FontSize) => {
    setPrefs(p => ({ ...p, fontSize }));
  }, []);

  const setLineHeight = useCallback((lineHeight: 'compact' | 'normal' | 'relaxed') => {
    setPrefs(p => ({ ...p, lineHeight }));
  }, []);

  return {
    isActive,
    prefs,
    toggle,
    enter,
    exit,
    setTheme,
    setFontFamily,
    setFontSize,
    setLineHeight,
  };
}

export function saveForOffline(url: string): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('caches' in window)) {
    return Promise.resolve(false);
  }
  return caches.open('theartofcto-v6').then(cache => {
    return fetch(url).then(response => {
      if (response.ok) {
        cache.put(url, response);
        return true;
      }
      return false;
    });
  }).catch(() => false);
}
